import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link, NavLink, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, BarChart3, Bot, Bug, Component, FileText, Hash, Type, Building2, CheckCircle2, Crosshair, Hourglass, Megaphone, Minus, RotateCcw, Repeat, Ticket, Timer,
  TrendingUp, UserCog, Users, Video, Waypoints, type LucideIcon,
} from 'lucide-react';
import { Avatar, Button } from '@vd/ui';
import { useAuth, useCurrentCompany } from '../features/auth/AuthProvider';
import {
  api, type CollectionKind, type FiledBy, type ReportCollection, type ReportErrors, type ReportOverview, type ReportPages, type ReportPatterns, type ReportPeople, type ReportTeam,
  type ReportTimeToFix, type ReportTrend, type TicketTypeKey,
} from '../lib/api';
import type { ApiError } from '../lib/apiClient';
import { When } from '../lib/When';
import {
  REPORT_PERIODS, comparison, downloadCsv, figure, filtersReady, formatDuration, previousLabel, readFilters, reportQuery, retryReport, toCsv, writeFilters,
  type ReportFilters, type ReportPeriodKind,
} from '../features/reports/reportFrame';
import { BarChart, type BarSeries } from '../features/reports/charts/BarChart';
import { InfoTip, useHoverTip } from '../features/reports/charts/HoverTip';
import { PageHeader, pageBackground } from '../features/shell/PageHeader';
import { pageNamer, patternFindings, type Finding } from '../features/reports/patternFindings';
import {
  collectionPagesTakeaway, collectionSummary, collectionWidgetsTakeaway, errorsTakeaway, outcomesTakeaway, overviewTakeaway, pagesTakeaway, patternsSummary, peopleTakeaway,
  teamSummary, teamTakeaway, ticketsSummary, timeToFixTakeaway, trendTakeaway, visitorLabel, visitorsTakeaway, visitorsUncertain,
} from '../features/reports/takeaways';
import { ConnectedSystems, PlanUsage, WorkspaceAtAGlance } from '../features/reports/WorkspaceCards';
import { TeamThisMonth } from '../features/reports/TeamThisMonth';
import { formatBytes } from '../features/dashboard/format';
import styles from './ReportsPage.module.css';
import { ProjectMark } from '../features/projects/ProjectMark';
import { RHYTHM_TIPS, RhythmTag } from '../features/reports/RhythmTag';
import { BarCell, useTableSort } from '../features/reports/tableSort';
import { EmptyState } from '../features/reports/EmptyState';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';
import { formatDay, formatWhen } from '../lib/time';

/**
 * The Reports pages, in the order the subnavigation shows them: Team,
 * Workspace, Tickets, Patterns (Jason, 24 Oct). Workspace is an owner's, and
 * is not a report: it holds what was the Dashboard's Workspace group, on any
 * plan.
 */
export const REPORT_SECTIONS = [
  { key: 'team', label: 'Team', icon: Users, owners: false },
  { key: 'workspace', label: 'Workspace', icon: Building2, owners: true },
  { key: 'tickets', label: 'Tickets', icon: Ticket, owners: false },
  { key: 'patterns', label: 'Patterns', icon: Waypoints, owners: false },
  // What the bug collection widget brought in, and who sent it (TOADO-964, Jason's name, 25 Oct).
  { key: 'collection', label: 'Bug collection', icon: Megaphone, owners: false },
] as const satisfies ReadonlyArray<{ key: string; label: string; icon: LucideIcon; owners: boolean }>;
export type ReportSection = (typeof REPORT_SECTIONS)[number]['key'];

/** `/reports` opens the first page, Team, keeping the filters and a link's anchor. */
export function ReportsIndex() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/reports/team', search: location.search, hash: location.hash }} replace />;
}

/**
 * Reports (TOADO-721): how the workspace is doing over a period, against the
 * one before it, on three pages: Team (team usage, people, opened and closed,
 * time to fix), Tickets (opened, bug and error hotspots) and Patterns,
 * and for owners Workspace (connected systems and plan usage, on any plan).
 * The filters live in the address and travel between the pages, so a link
 * opens the same report for a teammate who can see the same projects. Each
 * page asks only for its own reports. Team and up: below it the server
 * answers 402 and the page shows what Reports offers.
 */
export function ReportsPage() {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "Reports"] });
  const { section: raw } = useParams();
  const isOwner = useCurrentCompany()?.role === 'owner';
  const sections = REPORT_SECTIONS.filter((entry) => isOwner || !entry.owners);
  const section = sections.find((entry) => entry.key === raw)?.key;
  const { currentCompanyId } = useAuth();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const id = useId();
  // From the address as it is now, not as this render saw it, so two changes in
  // a row keep both. The router's own updater hands over the last render's
  // parameters, which lost From when To followed it quickly.
  const setFilters = (next: Partial<ReportFilters>) =>
    setParams(writeFilters({ ...readFilters(new URLSearchParams(window.location.search)), ...next }), { replace: true });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => api.listProjects(currentCompanyId!),
    enabled: !!currentCompanyId,
  });
  const ready = !!currentCompanyId && filtersReady(filters);
  const query = ready ? reportQuery(currentCompanyId!, filters) : '';
  const report = <T,>(name: string, read: (query: string) => Promise<T>, on: boolean) => ({
    queryKey: ['reports', name, query],
    queryFn: () => read(query),
    enabled: ready && on,
    retry: retryReport,
  });
  const onTickets = section === 'tickets';
  const onTeam = section === 'team';
  const overview = useQuery(report('overview', api.reports.overview, onTickets));
  const errors = useQuery(report('errors', api.reports.errors, onTickets));
  const pages = useQuery(report('pages', api.reports.pages, onTickets));
  // Tickets reads it too, to say which hotspot pages are chronic.
  const patterns = useQuery(report('patterns', api.reports.patterns, onTickets || section === 'patterns'));
  const trend = useQuery(report('trend', api.reports.trend, onTeam));
  const timeToFix = useQuery(report('time-to-fix', api.reports.timeToFix, onTeam));
  // Everyone: an owner sees the team, a member their own row (the server decides).
  const people = useQuery(report('people', api.reports.people, onTeam));
  // Owners only, on the server too: a member never asks for it.
  const team = useQuery(report('team', api.reports.team, onTeam && isOwner));
  const collection = useQuery(report('collection', api.reports.collection, section === 'collection'));
  const needsTeam = [overview, errors, pages, patterns, trend, timeToFix, people, collection]
    .some((result) => (result.error as ApiError | null)?.code === 'reports_requires_team');

  if (!section) return <Navigate to={{ pathname: '/reports/team', search: location.search }} replace />;
  const pageClass = `${styles.page} ${pageBackground}`;
  const header = <PageHeader icon={<BarChart3 size={18} />} title="Reports" testId="reports-header" />;
  if (!currentCompanyId) return <div className={pageClass} data-testid="reports-page">{header}<div className={styles.body}><p>Choose a workspace first.</p></div></div>;

  const subnav = (
    <nav className={styles.subnav} aria-label="Reports" data-testid="reports-subnav">
      {sections.map(({ key, label, icon: Icon }) => (
        <NavLink key={key} to={{ pathname: `/reports/${key}`, search: location.search }} className={styles.subnavLink}>
          <Icon size={15} aria-hidden="true" />{label}
        </NavLink>
      ))}
    </nav>
  );
  // Below Team the reports explain themselves; an owner keeps the way to Workspace.
  if (needsTeam && section !== 'workspace') {
    return (
      <div className={pageClass} data-testid="reports-page">
        {header}
        <div className={styles.body}>{isOwner && subnav}<ReportsNeedTeam companyId={currentCompanyId} /></div>
      </div>
    );
  }

  const against = previousLabel(filters.period, filters.from, filters.to);
  // The tab in one sentence, at the top of the body: the header carries no subtitle (Jason).
  const summary = section === 'tickets' ? ticketsSummary(overview.data, errors.data, against)
    : section === 'patterns' ? (patterns.data ? patternsSummary(patterns.data) : null)
    : section === 'team' ? teamSummary(trend.data, timeToFix.data)
    : section === 'collection' ? (collection.data ? collectionSummary(collection.data, against) : null)
    : null;
  return (
    <div className={pageClass} data-testid="reports-page">
      <PageHeader icon={<BarChart3 size={18} />} title="Reports" testId="reports-header"
        actions={section !== 'workspace' ? (
<div className={styles.filters} data-testid="report-filters">
          <div className={styles.filter}>
            <label htmlFor={`${id}-period`}>Period</label>
            <select id={`${id}-period`} value={filters.period} onChange={(event) => setFilters({ period: event.target.value as ReportPeriodKind })}>
              {REPORT_PERIODS.map((period) => <option key={period.value} value={period.value}>{period.label}</option>)}
            </select>
          </div>
          {filters.period === 'custom' && <>
            <div className={styles.filter}>
              <label htmlFor={`${id}-from`}>From</label>
              <input id={`${id}-from`} type="date" value={filters.from} max={filters.to || undefined} onChange={(event) => setFilters({ from: event.target.value })} />
            </div>
            <div className={styles.filter}>
              <label htmlFor={`${id}-to`}>To</label>
              <input id={`${id}-to`} type="date" value={filters.to} min={filters.from || undefined} onChange={(event) => setFilters({ to: event.target.value })} />
            </div>
          </>}
          <div className={styles.filter}>
            <label htmlFor={`${id}-project`}>Project</label>
            <select id={`${id}-project`} value={filters.projectId} onChange={(event) => setFilters({ projectId: event.target.value })}>
              <option value="">Whole workspace</option>
              {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </div>
        </div>
        ) : undefined} />
      <div className={styles.body}>
      {subnav}
      {summary && filtersReady(filters) && <p className={styles.summary} data-testid="report-summary">{summary}</p>}

      {section === 'workspace' ? (
        <div className={styles.tiles}>
          <section className={styles.tile} data-span={7} data-testid="report-systems" aria-label="Connected systems"><ConnectedSystems companyId={currentCompanyId} /></section>
          <div className={styles.tile} data-span={5}>
            <section data-testid="report-plan-usage" aria-label="Plan usage"><PlanUsage companyId={currentCompanyId} /></section>
            <section data-testid="report-workspace-summary" aria-label="Plan and people"><WorkspaceAtAGlance companyId={currentCompanyId} /></section>
          </div>
        </div>
      ) : !filtersReady(filters) ? <p className={styles.note}>Choose both days of the range, the first no later than the last.</p> : (
        <div className={styles.tiles}>
          {section === 'tickets' && <>
            <ReportCard span={5} title="Tickets opened" testId="report-overview" result={overview}>
              {(data) => <OverviewReport report={data} against={against} />}
            </ReportCard>
            <ReportCard span={7} title="Bug hotspots" testId="report-pages" anchor="pages" result={pages}>
              {(data) => <PagesReport report={data} against={against} rhythm={patterns.data?.rhythm} rhythmShort={patterns.data?.truncated ?? false} />}
            </ReportCard>
            <ReportCard span={12} title="Error hotspots" testId="report-errors" anchor="errors" result={errors}>
              {(data) => <ErrorsReport report={data} against={against} />}
            </ReportCard>
          </>}
          {section === 'patterns' && (
            <ReportCard span={12} title="Patterns" testId="report-patterns" result={patterns}>
              {(data) => <PatternsReport report={data} />}
            </ReportCard>
          )}
          {section === 'collection' && <>
            <ReportCard span={12} title="Reports by widget" testId="report-collection-widgets" result={collection}>
              {(data) => <CollectionWidgetsReport report={data} against={against} />}
            </ReportCard>
            <ReportCard span={12} title="Visitors" testId="report-collection-visitors" result={collection}>
              {(data) => <CollectionVisitorsReport report={data} />}
            </ReportCard>
            <ReportCard span={7} title="What happened to them" testId="report-collection-outcomes" result={collection}>
              {(data) => <CollectionOutcomesReport report={data} />}
            </ReportCard>
            <ReportCard span={5} title="Pages reported from" testId="report-collection-pages" result={collection}>
              {(data) => <CollectionPagesReport report={data} />}
            </ReportCard>
            {isOwner && collection.data?.recording && (
              <ReportCard span={12} title="Visitor recording" testId="report-collection-recording" result={collection}>
                {(data) => <CollectionRecordingReport recording={data.recording!} />}
              </ReportCard>
            )}
          </>}
          {/* Team usage, People, then Opened and closed (Jason, 24 Oct). */}
          {section === 'team' && <>
            {isOwner && (
              <ReportCard span={12} title="Team usage" testId="report-team" result={team}>
                {(data) => <TeamReport report={data} banner={<TeamThisMonth companyId={currentCompanyId} />} />}
              </ReportCard>
            )}
            <ReportCard span={12} title="People" testId="report-people" result={people}>
              {(data) => <PeopleReport report={data} isOwner={isOwner} />}
            </ReportCard>
            <ReportCard span={12} title="Opened and closed" testId="report-trend" result={trend}>
              {(data) => <TrendReport report={data} />}
            </ReportCard>
            <ReportCard span={12} title="Time to fix" testId="report-time-to-fix" result={timeToFix}>
              {(data) => <TimeToFixReport report={data} against={against} />}
            </ReportCard>
          </>}
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * One report, a tile in the page's grid: `span` is its share of twelve
 * columns, and a narrow page stacks every tile. It has no box of its own;
 * its tables do. Its loading state and its failure in words come first.
 */
function ReportCard<T>({ title, testId, anchor, span, result, children }: {
  title: string; testId: string; anchor?: string; span: 5 | 7 | 12; result: UseQueryResult<T, unknown>; children: (data: T) => ReactNode;
}) {
  // A link to this report (the Dashboard's teaser) brings it into view once it has loaded.
  const ref = useRef<HTMLElement>(null);
  const loaded = !!result.data;
  useEffect(() => {
    if (anchor && loaded && window.location.hash === `#${anchor}`) ref.current?.scrollIntoView({ block: 'start' });
  }, [anchor, loaded]);
  return (
    <section ref={ref} id={anchor} className={styles.tile} data-span={span} data-testid={testId} aria-label={title} aria-busy={result.isFetching}>
      {result.data ? children(result.data) : <>
        <h2 className={styles.cardTitle}>{title}</h2>
        {result.isError
          ? <p role="alert" className={styles.note}>Could not load this report: {(result.error as Error).message}</p>
          : <p role="status" className={styles.note}>Loading...</p>}
      </>}
    </section>
  );
}

/** A colour from the theme's highlight set, for a report's icon. */
type HeadTone = 'blue' | 'red' | 'amber' | 'violet' | 'green' | 'cyan' | 'magenta';

/** A report's heading, with what its numbers mean in one sentence under it (`takeaways.ts`). */
function CardHead({ title, icon: Icon, tone, info, takeaway, onExport, canExport }: {
  title: string; icon: LucideIcon; tone: HeadTone; info?: ReactNode; takeaway?: string | null; onExport: () => void; canExport: boolean;
}) {
  return (
    <header className={styles.cardHead} style={{ '--tone': `var(--hl-${tone})` } as CSSProperties}>
      <div className={styles.headText}>
        <h2 className={styles.cardTitle}>
          <span className={styles.headBadge} aria-hidden="true"><Icon size={15} /></span>
          {title}
          {info && <InfoTip label={title}>{info}</InfoTip>}
        </h2>
        {takeaway && <p className={styles.takeaway} data-testid="report-takeaway">{takeaway}</p>}
      </div>
      <Button variant="ghost" size="sm" onClick={onExport} disabled={!canExport}>Export CSV</Button>
    </header>
  );
}

/** A pane's heading: what it shows, and what that means, in an info bubble. */
function PaneHead({ title, info }: { title: string; info: ReactNode }) {
  return (
    <header className={styles.paneHead}>
      <h3 className={styles.paneTitle}>{title}</h3>
      <InfoTip label={title}>{info}</InfoTip>
    </header>
  );
}

const day = (at: string) => formatWhen(at, { dateOnly: true });
/** A week by its first day, a calendar date in the viewer's zone (the server's `week`), never shifted by a zone. */
const weekLabel = (date: string) => new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); // a day, not a moment (TOADO-904)
const share = (part: number, whole: number) => `${Math.round((whole > 0 ? part / whole : 0) * 100)}%`;

/** Tickets opened: the total, then each project as a bar, drawn to one scale in one neutral (TOADO-952). */
function OverviewReport({ report, against }: { report: ReportOverview; against: string }) {
  const exportCsv = () => downloadCsv('toado-tickets-opened.csv', toCsv(
    ['Project', 'Opened', 'Opened before'],
    report.byProject.map((row) => [row.project.name, row.opened.current, row.opened.previous]),
  ));
  const top = Math.max(1, ...report.byProject.flatMap((row) => [row.opened.current, row.opened.previous]));
  return <>
    <CardHead title="Tickets opened" icon={Ticket} tone="blue" takeaway={overviewTakeaway(report, against)} info={'Every ticket opened in the period, including ones archived since, against the period before, in all and per project. The mark on each bar is the period before.'} onExport={exportCsv} canExport={report.byProject.length > 0} />
    <p className={styles.headline}>
      <span className={styles.number}>{report.opened.current}</span>
      <span className={styles.compare}>{comparison(report.opened.current, report.opened.previous, against)}</span>
    </p>
    {report.byProject.length === 0 ? <EmptyState art="bars"><p>No tickets were opened in this period or the one before.</p></EmptyState> : (
      <table className={styles.barTable}>
        <caption className={styles.visuallyHidden}>Tickets opened per project, this period and the one before</caption>
        <thead className={styles.visuallyHidden}><tr><th scope="col">Project</th><th scope="col">Opened</th><th scope="col">Before</th></tr></thead>
        <tbody>
          {report.byProject.map((row) => (
            <tr key={row.project.id}>
              <td className={styles.barLabel}>
                <Link to={`/projects/${row.project.id}/board`} className={styles.project}>
                  <ProjectMark project={row.project} size={16} />{row.project.name}
                </Link>
              </td>
              <td className={styles.barCell}>
                <span className={styles.barTrack} aria-hidden="true">
                  <span className={styles.barNow} style={{ width: share(row.opened.current, top) }} />
                  <span className={styles.barBefore} style={{ left: share(row.opened.previous, top) }} title={`${row.opened.previous} before`} />
                </span>
                <span className={styles.count}>{row.opened.current}</span>
              </td>
              <td className={styles.barBeforeCount}>{row.opened.previous} before</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </>;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Ticket links shown under an error group before the rest are counted. */
const ERROR_TICKETS_SHOWN = 3;

/** Error hotspots (TOADO-734): the errors in the most tickets, each opening a ticket on its Errors tab. */
function ErrorsReport({ report, against }: { report: ReportErrors; against: string }) {
  const { coverage } = report;
  const exportCsv = () => downloadCsv('toado-error-hotspots.csv', toCsv(
    ['Error', 'Tickets', 'Projects', report.truncated ? 'First seen, among those read' : 'First seen', 'Last seen'],
    report.groups.map((group) => [group.example, figure(group.ticketCount, report.truncated), group.projects.map((project) => project.name).join('; '), group.firstSeen, group.lastSeen]),
  ));
  const top = Math.max(1, ...report.groups.map((group) => group.ticketCount));
  return <>
    <CardHead title="Error hotspots" icon={AlertTriangle} tone="amber" takeaway={errorsTakeaway(report)} info={'The JavaScript errors your captures caught in the most tickets this period, grouped by message. A group counts tickets, not how often the error fired. Errors from other browser extensions are left out.'} onExport={exportCsv} canExport={report.groups.length > 0} />
    <p className={styles.headline}>
      <span className={styles.number}>{coverage.withErrors.current}</span>
      <span className={styles.compare}>
        of {plural(coverage.tickets, 'ticket')} opened had captured errors, {comparison(coverage.withErrors.current, coverage.withErrors.previous, against)}
      </span>
    </p>
    <p className={styles.note}>{plural(coverage.captured, 'ticket')} in this period carried DevTools data at all; errors from other browser extensions are left out.</p>
    {report.truncated && <p className={styles.note}>This period has more errors than one report reads, so the oldest were left out. Choose a shorter period for all of them.</p>}
    {report.groups.length === 0 ? <EmptyState art="clear"><p>No errors were captured on tickets opened in this period.</p></EmptyState> : (
      <ol className={`${styles.box} ${styles.groups}`}>
        {report.groups.map((group) => {
          const shown = group.tickets.slice(0, ERROR_TICKETS_SHOWN);
          return (
            <li key={group.key} className={styles.group}>
              <span className={styles.groupCount}>
                <span className={styles.number}>{figure(group.ticketCount, report.truncated)}</span>
                <span className={styles.meter} aria-hidden="true"><span style={{ width: share(group.ticketCount, top) }} /></span>
              </span>
              <span className={styles.groupBody}>
                <code className={styles.message}>{group.example}</code>
                <span className={styles.groupMeta}>
                  {report.truncated ? `${figure(group.ticketCount, true)} tickets` : plural(group.ticketCount, 'ticket')} across {group.projects.map((project) => project.name).join(', ')}
                  {' · '}{report.truncated ? 'first among those read' : 'first'} {day(group.firstSeen)}, <When at={group.lastSeen} prefix="last " />
                </span>
                <ul className={styles.ticketLinks}>
                  {shown.map((ticket) => (
                    <li key={ticket.id}><Link to={`/tickets/${ticket.id}?devtools=errors`}>{ticket.title}</Link></li>
                  ))}
                  {group.ticketCount > shown.length && <li className={styles.note}>and {figure(group.ticketCount - shown.length, report.truncated)} more</li>}
                </ul>
              </span>
            </li>
          );
        })}
      </ol>
    )}
  </>;
}

/**
 * Bug hotspots (TOADO-735, made visual on 16 Oct): each project as a tile,
 * with its count against the period before, how many were bugs, how many
 * pages drew them and its top pages; then every page, in a table that opens
 * on request. Chronic pages (TOADO-862) lead the table and are marked.
 */
function PagesReport({ report, against, rhythm, rhythmShort }: { report: ReportPages; against: string; rhythm?: ReportPatterns['rhythm']; rhythmShort: boolean }) {
  // Chronic or spike from Patterns (TOADO-862), once it has loaded; chronic pages lead, the rest keep their order.
  const rhythmOf = new Map((rhythm ?? []).map((entry) => [entry.page, entry]));
  const rank = (page: string) => (rhythmOf.get(page)?.rhythm === 'chronic' ? 0 : 1);
  const rows = [...report.pages].sort((x, y) => rank(x.page) - rank(y.page));
  // Read short, the newest tickets are kept, so the period before is the one cut:
  // its counts would make any rise, so none is drawn (Codex review round 14).
  const short = report.truncated;
  const change = (current: number, previous: number) => (short ? 'Not compared: too many to read' : comparison(current, previous, against));
  const exportCsv = () => downloadCsv('toado-bug-hotspots-by-page.csv', toCsv(
    ['Page', 'Opened', 'Opened before'],
    [
      ...report.pages.map((page) => [page.page, figure(page.current, short), short ? 'not read in full' : page.previous] as Array<string | number>),
      ...(report.noPage.current + report.noPage.previous > 0 ? [['(no page)', figure(report.noPage.current, short), short ? 'not read in full' : report.noPage.previous]] : []),
    ],
  ));
  const empty = report.pages.length === 0 && report.noPage.current === 0;
  return <>
    <CardHead title="Bug hotspots" icon={Bug} tone="red" takeaway={pagesTakeaway(report, rhythm)} info={`Where tickets came from this period, by project: how many, how many were bugs, and the pages that drew them. Chronic: ${RHYTHM_TIPS.chronic} Spike: ${RHYTHM_TIPS.spike}`} onExport={exportCsv} canExport={!empty} />
    {report.truncated && <p className={styles.note}>This period has more tickets than one report reads, so the oldest were left out. Choose a shorter period for all of them.</p>}
    {empty ? <EmptyState art="pages"><p>No tickets were opened in this period.</p></EmptyState> : <>
      <div className={styles.projectTiles} data-testid="hotspot-projects">
        {report.byProject.map((row) => (
          <article key={row.project.id} className={styles.projectTile} aria-label={row.project.name}>
            <Link to={`/projects/${row.project.id}/board`} className={styles.project}>
              <ProjectMark project={row.project} size={16} />{row.project.name}
            </Link>
            <p className={styles.tileFigure}>
              <span className={styles.number}>{figure(row.current, short)}</span>
              <span className={styles.changeLine}>{change(row.current, row.previous)}</span>
            </p>
            <p className={styles.tileFact}>
              <span>{figure(row.bugs, short)} bugs</span>
              <span className={styles.meter} aria-hidden="true"><span style={{ width: share(row.bugs, row.current) }} /></span>
            </p>
            <p className={styles.tileFact}>{row.pageCount === 0 ? 'No page recorded' : `On ${plural(row.pageCount, 'page')}`}</p>
            {row.topPages.length > 0 && (
              <ul className={styles.topPages}>
                {row.topPages.map((entry) => (
                  <li key={entry.page}>
                    <span className={styles.pageAddress}>{entry.page}</span>
                    {rhythmOf.has(entry.page) && <RhythmTag entry={rhythmOf.get(entry.page)!} />}
                    <span className={styles.count}>{figure(entry.tickets, short)}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
      <details className={styles.dataTable}>
        <summary>Every page ({report.pages.length}{report.noPage.current > 0 ? ', and those with none' : ''})</summary>
        <div className={`${styles.box} ${styles.tableWrap}`}>
          <table className={styles.table}>
            <thead><tr><th scope="col">Page</th><th scope="col">Opened</th><th scope="col">Change</th></tr></thead>
            <tbody>
              {rows.map((page) => (
                <tr key={page.page}>
                  <td>
                    <span className={styles.pageAddress}>{page.page}</span>
                    {rhythmOf.has(page.page) && <RhythmTag entry={rhythmOf.get(page.page)!} />}
                    <ul className={styles.ticketLinks}>
                      {page.tickets.map((ticket) => <li key={ticket.id}><Link to={`/tickets/${ticket.id}`}>{ticket.title}</Link></li>)}
                      {page.current > page.tickets.length && <li className={styles.note}>and {figure(page.current - page.tickets.length, short)} more</li>}
                    </ul>
                  </td>
                  <td className={styles.count}>{figure(page.current, short)}</td>
                  <td className={styles.change}>{change(page.current, page.previous)}</td>
                </tr>
              ))}
              {report.noPage.current > 0 && (
                <tr>
                  <td><span className={styles.note}>No page (made by hand)</span></td>
                  <td className={styles.count}>{figure(report.noPage.current, short)}</td>
                  <td className={styles.change}>{change(report.noPage.current, report.noPage.previous)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </details>
    </>}
  </>;
}

const TYPE_SERIES: BarSeries[] = [
  { key: 'bug', label: 'Bug', tone: 'red' },
  { key: 'improvement', label: 'Improvement', tone: 'blue' },
  { key: 'feature', label: 'Feature', tone: 'violet' },
  { key: 'other', label: 'Other type', tone: 'amber' },
  { key: 'untyped', label: 'No type', tone: 'grey' },
] satisfies Array<BarSeries & { key: TicketTypeKey }>;

const ELEMENT_KIND: Record<ReportPatterns['sameElement'][number]['element']['kind'], string> = {
  testId: 'test id', component: 'component', text: 'tag and text',
};
/** How the element was found, as an icon beside it. */
const ELEMENT_ICON: Record<ReportPatterns['sameElement'][number]['element']['kind'], LucideIcon> = {
  testId: Hash, component: Component, text: Type,
};

/** Each finding's heading, icon and colour. */
const FINDING_LOOK: Record<Finding['key'], { title: string; icon: LucideIcon; tone: string }> = {
  element: { title: 'The same element', icon: Crosshair, tone: 'var(--hl-violet)' },
  chronic: { title: 'Keeps coming back', icon: Repeat, tone: 'var(--hl-red)' },
  reopens: { title: "Fixes that didn't stick", icon: RotateCcw, tone: 'var(--hl-amber)' },
};

/** The weeks of the rhythm window, drawn as a meter. */
const RHYTHM_WEEKS = 8;

/**
 * Patterns (TOADO-862, its own page since 16 Oct): three findings as tiles,
 * then where tickets land by area and type beside which pages keep coming
 * back, then the same element again beside who has the reopened tickets.
 */
function PatternsReport({ report }: { report: ReportPatterns }) {
  const findings = patternFindings(report);
  const name = pageNamer(report);
  // Every figure from the tickets is at least when they were read short; the
  // reopen total and per-person counts are whole, their ranking by page is not.
  const short = report.truncated;
  const exact = (lowerBound: boolean) => (lowerBound ? 'at least' : 'exact');
  const exportCsv = () => downloadCsv('toado-patterns.csv', toCsv(
    ['Finding', 'Page or area', 'What', 'Count', 'Of', 'People or weeks', 'Counts'],
    [
      ...report.sameElement.map((entry) => ['Same element', entry.page, entry.element.label, entry.tickets, 'tickets', entry.people, exact(short)] as Array<string | number>),
      ...report.rhythm.map((entry) => [entry.rhythm === 'chronic' ? 'Chronic page' : 'Spike', entry.page, '', entry.tickets, 'tickets', entry.weeks, exact(short)] as Array<string | number>),
      ...report.areas.map((area) => ['Area', area.area,
        TYPE_SERIES.map((part) => `${part.label} ${area.byType[part.key as TicketTypeKey]}`).join('; '), area.total, 'tickets', '', exact(short)] as Array<string | number>),
      ...report.reopens.byPage.map((entry) => ['Reopened', entry.page ?? '(no page)', '', entry.reopens, 'reopens', '', exact(report.reopens.truncated)] as Array<string | number>),
    ],
  ));
  const areasShown = report.counts.areas <= report.areas.length ? ''
    : short ? ` (${report.areas.length} of ${figure(report.counts.areas, true)} areas, ranked by the tickets read)`
    : ` (the ${report.areas.length} largest of ${report.counts.areas} areas)`;
  const peopleShown = report.reopens.people > report.reopens.byPerson.length ? ` (the ${report.reopens.byPerson.length} with most, of ${report.reopens.people} people)` : '';
  const anything = report.sameElement.length + report.rhythm.length + report.areas.length + report.reopens.byPage.length > 0;
  return <>
    <CardHead title="Patterns" icon={Waypoints} tone="violet" info="Three things worth knowing about this period's tickets, in words, then the evidence: where tickets land, which pages keep coming back, the same element again and again, and fixes that did not stick." onExport={exportCsv} canExport={anything} />
    {(short || report.reopens.truncated) && <p className={styles.note}>This period has more tickets than one report reads, so figures drawn from them are at least what is shown (a "+" says so). The reopen total and each person's count are complete. Choose a shorter period or one project for all of them.</p>}
    <ul className={styles.findings} data-testid="pattern-findings">
      {findings.map((finding) => {
        const look = FINDING_LOOK[finding.key];
        const Icon = look.icon;
        return (
          <li key={finding.key} data-testid={`finding-${finding.key}`} className={styles.finding} style={{ '--tone': look.tone } as CSSProperties}>
            <h3 className={styles.findingTitle}><span className={styles.findingIcon} aria-hidden="true"><Icon size={15} /></span>{look.title}</h3>
            <p>{finding.text}</p>
            {finding.tickets.length > 0 && (
              <ul className={styles.ticketLinks}>
                {finding.tickets.slice(0, 5).map((ticket) => <li key={ticket.id}><Link to={`/tickets/${ticket.id}`}>{ticket.title}</Link></li>)}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
    <div className={styles.panes}>
      <section className={styles.pane} aria-label="Tickets by area of the app">
        <PaneHead title={`Tickets by area of the app, and what kind${areasShown}`} info={<>
          Every ticket this period with a page, grouped by the area of the app it came from (the start of its address), split by
          ticket type. Point at a color for its count and share; point at an area for the pages in it.
        </>} />
        <div className={styles.paneBody} data-scroll-pane>
          <BarChart
            hideCaption
            caption={`Tickets by area of the app, and what kind${areasShown}`}
            testId="chart-areas"
            series={TYPE_SERIES}
            lowerBound={short}
            rows={report.areas.map((area) => ({
              key: area.area, label: name(area.area), values: area.byType,
              detail: area.pages.map((entry) => `${name(entry.page)} (${figure(entry.tickets, short)})`).join(', ')
                + (area.pageCount > area.pages.length ? `, and ${figure(area.pageCount - area.pages.length, short)} more` : ''),
            }))}
            empty={<p className={styles.note}>{short
              ? 'No tickets with a page among those read, and there were too many to read them all. Choose one project or a shorter period.'
              : 'No tickets with a page were opened in this period.'}</p>}
          />
        </div>
      </section>
      <section className={styles.pane} aria-label="Pages by how often they come back">
        <PaneHead title={`Pages by how often they come back, over the last ${RHYTHM_WEEKS} weeks`} info={<>
          A page is chronic when it had tickets in three or more of the last {RHYTHM_WEEKS} weeks: a part of the product that keeps
          failing. It is a spike when every one of its tickets came in a single week: something that went wrong once. Each square is
          a week with tickets.
        </>} />
        <div className={styles.paneBody} data-scroll-pane>
          <RhythmList report={report} name={name} short={short} />
        </div>
      </section>
    </div>
    {(report.sameElement.length > 0 || report.reopens.byPerson.length > 0) && (
      <div className={styles.split}>
        {report.sameElement.length > 0 ? <section aria-label="The same element again">
          <PaneHead title={`The same element again${report.counts.sameElement > report.sameElement.length ? ` (${report.sameElement.length} of ${figure(report.counts.sameElement, short)})` : ''}`} info={<>
            One element on a page (a button, a field) that three or more tickets this period were pinned to, found by its test id,
            its component, or its tag and text. Many people pointing at the same thing is a strong sign it is broken.
          </>} />
          <div className={`${styles.box} ${styles.tableWrap}`}>
            <table className={styles.table}>
              {/* Project, page, element, then the counts: where it is, narrowing down, before how much (Jason, 16 Oct). */}
              <thead><tr><th scope="col">Project</th><th scope="col" className={styles.leftHead}>Page</th><th scope="col" className={styles.leftHead}>Element</th><th scope="col">Tickets</th><th scope="col">People</th></tr></thead>
              <tbody>
                {report.sameElement.map((entry) => {
                  const KindIcon = ELEMENT_ICON[entry.element.kind];
                  return (
                  <tr key={`${entry.page} ${entry.element.kind} ${entry.element.label}`}>
                    <td>
                      {(entry.projects ?? []).map((project) => (
                        <span key={project.id} className={styles.project}><ProjectMark project={project} size={16} />{project.name}</span>
                      ))}
                    </td>
                    <td><span className={styles.pageAddress}>{name(entry.page)}</span></td>
                    <td>
                      <span className={styles.element} title={`Found by its ${ELEMENT_KIND[entry.element.kind]}`}>
                        <KindIcon size={12} aria-hidden="true" /><code>{entry.element.label}</code>
                      </span>
                      {entry.examples.length > 0 && (
                        <ul className={styles.ticketChips} aria-label="Tickets on it">
                          {entry.examples.map((ticket) => (
                            <li key={ticket.id}><Link to={`/tickets/${ticket.id}`} className={styles.ticketChip}><Ticket size={12} aria-hidden="true" />{ticket.title}</Link></li>
                          ))}
                          {entry.tickets > entry.examples.length && <li className={styles.note}>and {figure(entry.tickets - entry.examples.length, short)} more</li>}
                        </ul>
                      )}
                    </td>
                    <td className={styles.count}>{figure(entry.tickets, short)}</td>
                    <td className={styles.count}>{figure(entry.people, short)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section> : null}
        {report.reopens.byPerson.length > 0 && (
          <section aria-label="Reopened">
            <PaneHead title="Reopened, by who has the ticket now" info={<>
              Tickets moved back out of their project's last column (Done) to another column: a fix that did not stick. Counted
              against whoever the ticket is assigned to now.
            </>} />
            <BarChart hideCaption caption={`Reopened, by who has the ticket now${peopleShown}`} testId="chart-reopens" series={[{ key: 'reopens', label: 'Reopens', tone: 'amber' }]}
              rows={report.reopens.byPerson.map((person) => ({ key: person.userId, label: person.name, values: { reopens: person.reopens } }))} />
          </section>
        )}
      </div>
    )}
    <p className={styles.note}>
      An element counts with three or more tickets on one page. A page is chronic with tickets in three of the last 8 weeks, a spike with all of them in one.
      A reopen is a move out of a project's last column to another of its columns, counted from {formatDay(report.reopens.countedFrom)}.
    </p>
  </>;
}

/** Pages by how often they come back: each with its tag, a square per week with tickets, and the figures on hover. */
function RhythmList({ report, name, short }: { report: ReportPatterns; name: (page: string) => string; short: boolean }) {
  const tip = useHoverTip();
  return (
    <figure className={styles.rhythm} data-testid="chart-rhythm">
      <figcaption className={styles.visuallyHidden}>Pages by how often they come back, over the last {RHYTHM_WEEKS} weeks</figcaption>
      {report.rhythm.length === 0 ? <p className={styles.note}>No page is chronic or a spike.</p> : (
        <ul className={styles.rhythmList}>
          {report.rhythm.map((entry) => (
            <li key={entry.page} {...tip.bind(`${entry.rhythm === 'chronic' ? 'Chronic' : 'Spike'} · ${entry.page}\nTickets in ${short ? 'at least ' : ''}${entry.weeks} of the last ${RHYTHM_WEEKS} weeks\n${plural(entry.tickets, 'ticket')} in all`)}>
              <span className={styles.pageAddress}>{name(entry.page)}</span>
              <RhythmTag entry={entry} />
              <span className={styles.weeks} aria-label={`Tickets in ${short ? 'at least ' : ''}${entry.weeks} of ${RHYTHM_WEEKS} weeks`} data-rhythm={entry.rhythm}>
                {/* The weeks themselves, oldest on the left, each as dark as its share of the busiest (16 Oct); an older server sends only how many. */}
                {entry.byWeek
                  ? entry.byWeek.map((count, index) => (
                    <span key={index} data-on={count > 0 || undefined} data-week={index}
                      style={count > 0 ? { opacity: 0.45 + 0.55 * (count / Math.max(...entry.byWeek!)) } : undefined}
                      title={`Week ending ${day(new Date(new Date(report.period.to).getTime() - (RHYTHM_WEEKS - 1 - index) * 7 * 86_400_000 - 1).toISOString())}: ${plural(count, 'ticket')}`} />
                  ))
                  : Array.from({ length: RHYTHM_WEEKS }, (_, index) => <span key={index} data-on={index < entry.weeks || undefined} />)}
              </span>
              <span className={styles.rhythmMeta}>{figure(entry.weeks, short)} of {RHYTHM_WEEKS} weeks · {plural(entry.tickets, 'ticket')}</span>
            </li>
          ))}
        </ul>
      )}
      {tip.node}
    </figure>
  );
}

const FILED_SERIES: Array<BarSeries & { key: FiledBy }> = [
  { key: 'extension', label: 'Extension', tone: 'blue' },
  { key: 'walkthrough', label: 'Walkthrough', tone: 'violet' },
  { key: 'teamWidget', label: 'Team widget', tone: 'cyan' },
  { key: 'manual', label: 'By hand', tone: 'grey' },
  { key: 'agent', label: 'AI agent', tone: 'magenta' },
];

/** People (TOADO-862): who logs and how, who closes, who carries the queue, who sends to Jira. A member sees their own row. */
function PeopleReport({ report, isOwner }: { report: ReportPeople; isOwner: boolean }) {
  const location = useLocation();
  const measuresMentions = report.people.some((person) => person.mentioned !== undefined);
  const active = report.people.filter((person) => person.openedTotal + person.closed + person.carrying + person.sentToJira + (person.mentioned ?? 0) > 0);
  const single = (key: 'closed' | 'carrying' | 'sentToJira' | 'mentioned') => active.map((person) => ({ key: person.userId, label: person.name, values: { [key]: person[key] ?? 0 } }));
  const exportCsv = () => downloadCsv('toado-people.csv', toCsv(
    ['Person', 'Role', ...FILED_SERIES.map((part) => `Opened: ${part.label}`), 'Opened', 'Closed while theirs', 'Carrying now', 'Sent to Jira',
      // Only when the API measured it: an unmeasured count is not a zero (Codex review round 6).
      ...(measuresMentions ? ['Mentioned'] : []), 'Closes counted from', 'Jira sends counted from'],
    // What each count covers travels with it (Codex review round 14).
    report.people.map((person) => [person.name, person.role, ...FILED_SERIES.map((part) => person.opened[part.key]), person.openedTotal, person.closed, person.carrying, person.sentToJira,
      ...(measuresMentions ? [person.mentioned ?? ''] : []),
      report.closesCountedFrom ?? 'none recorded', report.jiraCountedFrom ?? 'none credited']),
  ));
  const none = <p className={styles.note}>{isOwner ? 'Nothing recorded for anyone in this period.' : 'Nothing recorded for you in this period.'}</p>;
  return <>
    <CardHead title="People" icon={Users} tone="cyan" takeaway={peopleTakeaway(report)} info={`For each person: the tickets they opened and how, the tickets they closed while assigned, the open tickets assigned to them now${measuresMentions ? ', the tickets they sent to Jira, and the tickets that mentioned them' : ', and the tickets they sent to Jira'}.`} onExport={exportCsv} canExport={report.people.length > 0} />
    {!isOwner && <p className={styles.note}>Your own numbers. An owner sees the whole team here.</p>}
    {/* Tickets opened, and how, the card's full width; the three one-figure charts side by side under it (Jason, 16 Oct). */}
    {active.length === 0 ? none : <div className={styles.chartsStack}>
      <BarChart caption="Tickets opened, and how" testId="chart-opened" series={FILED_SERIES}
        rows={active.map((person) => ({ key: person.userId, label: person.name, values: person.opened }))} />
      <div className={styles.chartsRow}>
        <BarChart caption="Closed while theirs" testId="chart-closed" series={[{ key: 'closed', label: 'Closed', tone: 'green' }]} rows={single('closed')} />
        <BarChart caption="Carrying the queue now" testId="chart-carrying" series={[{ key: 'carrying', label: 'Open and assigned', tone: 'amber' }]} rows={single('carrying')} />
        <BarChart caption="Sent to Jira" testId="chart-jira" series={[{ key: 'sentToJira', label: 'Sent', tone: 'blue' }]} rows={single('sentToJira')} />
        {measuresMentions && <BarChart caption="Mentioned" testId="chart-mentioned" series={[{ key: 'mentioned', label: 'Tickets', tone: 'violet' }]} rows={single('mentioned')} />}
      </div>
    </div>}
    <p className={styles.note}>
      {report.fromVisitors > 0 && <>Plus {report.fromVisitors} from visitors through the bug collection widget or a public link, which no one person filed: see{' '}
        <Link to={{ pathname: '/reports/collection', search: location.search }} data-testid="people-to-collection">Bug collection</Link>. </>}
      {measuresMentions && <>Mentioned counts the tickets opened in the period whose description names them, and the tickets with a comment written in the period that does.{' '}</>}
      Closed and carrying count tickets assigned to them now.{' '}
      {report.jiraCountedFrom
        ? <>Sent to Jira counts from {formatDay(report.jiraCountedFrom)}.</>
        : <>No send to Jira is credited to anyone yet; sends from before this release were not recorded with who asked.</>}
    </p>
    <CountedFrom at={report.closesCountedFrom} />
  </>;
}

/** Where closes start counting, said on both reports that read them. */
function CountedFrom({ at }: { at: string | null }) {
  return (
    <p className={styles.note}>
      {at
        ? <>Counting closes from {formatDay(at)}, when moves were first recorded. </>
        : <>No moves recorded yet, so nothing has closed. </>}
      Closed means it reached its project's last column and is still there; one archived without getting there is not.
    </p>
  );
}

const CHART_HEIGHT = 240;
/** Room above the plot for the top figure of the scale. */
const HEAD = 12;
/** Room on the left for the scale's figures. */
const AXIS = 36;

/** The width an element is drawn at, kept up to date as it changes. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Four steps to a top a little over `max`, each a round figure. */
function scaleSteps(max: number): number[] {
  const raw = Math.max(1, max) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= raw) ?? raw;
  const whole = Math.max(1, Math.ceil(step));
  return [0, whole, whole * 2, whole * 3, whole * 4];
}

/**
 * Trend (TOADO-736, drawn larger on 16 Oct): opened and closed per week as
 * bars and what was open as a line, across the page's width on one scale with
 * its figures on the left; pointing at a week gives its numbers. The same as
 * a table below.
 */
function TrendReport({ report }: { report: ReportTrend }) {
  const { weeks } = report;
  const [frame, width] = useWidth<HTMLDivElement>();
  const tip = useHoverTip();
  const exportCsv = () => downloadCsv('toado-opened-and-closed.csv', toCsv(
    ['Week of', 'Opened', 'Closed', 'Open at the end of the week'],
    weeks.map((week) => [week.week, week.opened, week.closed, week.backlog ?? '']),
  ));
  const ticks = scaleSteps(Math.max(...weeks.flatMap((week) => [week.opened, week.closed, week.backlog ?? 0])));
  const top = ticks[ticks.length - 1]!;
  const plot = Math.max(width - AXIS, weeks.length * 40);
  const slot = weeks.length > 0 ? plot / weeks.length : plot;
  const bar = Math.max(6, Math.min(28, slot * 0.28));
  const y = (value: number) => HEAD + CHART_HEIGHT - (value / top) * CHART_HEIGHT;
  const x = (index: number) => AXIS + index * slot + slot / 2;
  const opened = weeks.reduce((sum, week) => sum + week.opened, 0);
  const closed = weeks.reduce((sum, week) => sum + week.closed, 0);
  const lastBacklog = weeks.length > 0 ? weeks[weeks.length - 1]!.backlog : null;
  // The line only where there is a figure: a week before closes were counted has none.
  const counted = weeks.map((week, index) => ({ week, index })).filter(({ week }) => week.backlog !== null);
  return <>
    <CardHead title="Opened and closed" icon={TrendingUp} tone="blue" takeaway={trendTakeaway(report)}
      info="Tickets opened and closed each week of the period, and the dashed line is how many were open at the end of each week. Closed means a ticket reached its project's last column."
      onExport={exportCsv} canExport={weeks.length > 0} />
    <p className={styles.headline}>
      <span className={styles.number}>{opened}</span>
      <span className={styles.compare}>
        opened, {closed} closed{lastBacklog !== null ? `, ${lastBacklog} open at the end of the period` : ''}
      </span>
    </p>
    {weeks.length > 0 && <>
      <div className={styles.trendFrame} ref={frame}>
        {/* The table below holds the same figures for a screen reader. */}
        <svg className={styles.chart} width={AXIS + plot} height={HEAD + CHART_HEIGHT + 24} aria-hidden="true">
          {ticks.map((tick) => <line key={`grid-${tick}`} className={styles.grid} x1={AXIS} x2={AXIS + plot} y1={y(tick)} y2={y(tick)} />)}
          {weeks.map((week, index) => (
            <g key={week.week} {...tip.bind(`Week of ${weekLabel(week.week)}\nOpened: ${week.opened}\nClosed: ${week.closed}${week.backlog !== null ? `\nOpen at the end: ${week.backlog}` : ''}`)}>
              <rect className={styles.hoverSlot} x={AXIS + index * slot} y={HEAD} width={slot} height={CHART_HEIGHT} />
              <rect className={styles.barOpened} x={x(index) - bar - 1} y={y(week.opened)} width={bar} height={HEAD + CHART_HEIGHT - y(week.opened)} rx={2} />
              <rect className={styles.barClosed} x={x(index) + 1} y={y(week.closed)} width={bar} height={HEAD + CHART_HEIGHT - y(week.closed)} rx={2} />
              <text className={styles.axis} x={x(index)} y={HEAD + CHART_HEIGHT + 17} textAnchor="middle">{weekLabel(week.week)}</text>
            </g>
          ))}
          {counted.length > 1 && (
            <polyline className={styles.backlog} fill="none"
              points={counted.map(({ week, index }) => `${x(index)},${y(week.backlog!)}`).join(' ')} />
          )}
          {counted.map(({ week, index }) => <circle key={`dot-${week.week}`} className={styles.backlogDot} cx={x(index)} cy={y(week.backlog!)} r={3} />)}
          {ticks.map((tick) => <text key={`tick-${tick}`} className={styles.axis} x={AXIS - 8} y={y(tick) + 3} textAnchor="end">{tick}</text>)}
        </svg>
        {tip.node}
      </div>
      <p className={styles.legend} aria-hidden="true">
        <span><i className={styles.keyOpened} /> Opened</span>
        <span><i className={styles.keyClosed} /> Closed</span>
        <span><i className={styles.keyBacklog} /> Open at the end of the week</span>
      </p>
      <details className={styles.dataTable}>
        <summary>Show as a table</summary>
        <div className={`${styles.box} ${styles.tableWrap}`}>
          <table className={styles.table}>
            <caption className={styles.visuallyHidden}>Tickets opened, closed and open at the end of each week</caption>
            <thead><tr><th scope="col">Week of</th><th scope="col">Opened</th><th scope="col">Closed</th><th scope="col">Open at the end</th></tr></thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week.week}>
                  <th scope="row">{weekLabel(week.week)}</th>
                  <td className={styles.count}>{week.opened}</td>
                  <td className={styles.count}>{week.closed}</td>
                  <td className={styles.count}>{week.backlog ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>}
    <CountedFrom at={report.countedFrom} />
  </>;
}

/** Faster, slower or the same as the period before, as a coloured chip. */
function Change({ current, previous }: { current: number | null; previous: number | null }) {
  if (current === null || previous === null || Math.round(current / 60) === Math.round(previous / 60)) return null;
  const faster = current < previous;
  const Icon = faster ? ArrowDownRight : ArrowUpRight;
  return <span className={styles.changeChip} data-way={faster ? 'faster' : 'slower'}><Icon size={12} aria-hidden="true" />{faster ? 'Faster' : 'Slower'}</span>;
}

/** One figure as a tile: its icon and colour, the figure, what it is, and how it compares. */
function Stat({ icon: Icon, tone, label, value, chip, detail, testId }: {
  icon: LucideIcon; tone: HeadTone; label: string; value: ReactNode; chip?: ReactNode; detail?: ReactNode; testId?: string;
}) {
  return (
    <div className={styles.stat} style={{ '--tone': `var(--hl-${tone})` } as CSSProperties} data-testid={testId}>
      <span className={styles.statLabel}><Icon size={14} aria-hidden="true" />{label}</span>
      <span className={styles.statValue}>{value}{chip}</span>
      {detail && <span className={styles.statDetail}>{detail}</span>}
    </div>
  );
}

/**
 * Time to fix (TOADO-737, laid out on 16 Oct): the typical time, the slowest
 * tenth and the tickets closed as tiles, each against the period before;
 * then each project, its slowest tickets under its name.
 */
function TimeToFixReport({ report, against }: { report: ReportTimeToFix; against: string }) {
  const byProject = useTableSort(report.byProject, {
    project: (row) => row.project.name, closed: (row) => row.current.closed,
    typical: (row) => row.current.medianSeconds, slowest: (row) => row.current.p90Seconds,
  });
  const mostClosed = Math.max(0, ...report.byProject.map((row) => row.current.closed));
  const { overall } = report;
  const hours = (seconds: number | null) => (seconds === null ? '' : Math.round(seconds / 36) / 100);
  const exportCsv = () => downloadCsv('toado-time-to-fix.csv', toCsv(
    ['Project', 'Closed', 'Median hours', '90th percentile hours', 'Median hours before', '90th percentile hours before'],
    report.byProject.map((row) => [
      row.project.name, row.current.closed, hours(row.current.medianSeconds), hours(row.current.p90Seconds),
      hours(row.previous.medianSeconds), hours(row.previous.p90Seconds),
    ]),
  ));
  /** How a time compares with the same time the period before: faster is better. */
  const change = (current: number | null, previous: number | null) => {
    if (current === null) return '';
    if (previous === null) return `nothing to compare with in ${against}`;
    if (Math.round(current / 60) === Math.round(previous / 60)) return `the same as ${against}`;
    return `${current < previous ? 'faster' : 'slower'} than ${against} (${formatDuration(previous)})`;
  };
  return <>
    <CardHead title="Time to fix" icon={Timer} tone="green" takeaway={timeToFixTakeaway(report, against)}
      info="From when a ticket was captured to when it reached its project's last column. Typical is the median; the slowest tenth is the time nine in ten tickets beat."
      onExport={exportCsv} canExport={report.byProject.length > 0} />
    <div className={styles.stats}>
      <Stat icon={Timer} tone="green" label="Typical" testId="fix-typical" value={formatDuration(overall.current.medianSeconds)}
        chip={<Change current={overall.current.medianSeconds} previous={overall.previous.medianSeconds} />}
        detail={`typical, over ${plural(overall.current.closed, 'ticket')} closed; ${change(overall.current.medianSeconds, overall.previous.medianSeconds)}`} />
      <Stat icon={Hourglass} tone="amber" label="Slowest tenth" testId="fix-slowest" value={formatDuration(overall.current.p90Seconds)}
        chip={<Change current={overall.current.p90Seconds} previous={overall.previous.p90Seconds} />}
        detail={overall.current.p90Seconds !== null
          ? `The slowest tenth took ${formatDuration(overall.current.p90Seconds)} or more; ${change(overall.current.p90Seconds, overall.previous.p90Seconds)}.`
          : undefined} />
      <Stat icon={CheckCircle2} tone="blue" label="Closed" testId="fix-closed" value={overall.current.closed}
        detail={`${overall.previous.closed} in ${against}`} />
    </div>
    {report.byProject.length === 0 ? <EmptyState art="clock"><p>No tickets were closed in this period.</p></EmptyState> : (
      <div className={`${styles.box} ${styles.tableWrap}`} data-testid="fix-by-project">
        <table className={styles.table}>
          <thead><tr>
            {byProject.head('project', 'Project', { left: true, numeric: false })}{byProject.head('closed', 'Closed')}{byProject.head('typical', 'Typical')}{byProject.head('slowest', 'Slowest tenth')}
          </tr></thead>
          <tbody>
            {byProject.rows.map((row) => (
              <tr key={row.project.id}>
                <td>
                  <span className={styles.project}><ProjectMark project={row.project} size={16} />{row.project.name}</span>
                  {row.slowest.length > 0 && (
                    <ul className={styles.slowest}>
                      {row.slowest.map((ticket) => (
                        <li key={ticket.id}><Link to={`/tickets/${ticket.id}`}>{ticket.title}</Link><span className={styles.duration}>{formatDuration(ticket.seconds)}</span></li>
                      ))}
                    </ul>
                  )}
                </td>
                <BarCell value={row.current.closed} max={mostClosed} tone="var(--chart-green)" />
                <td className={styles.count}>
                  <span className={styles.cellFigure}>{formatDuration(row.current.medianSeconds)}<Change current={row.current.medianSeconds} previous={row.previous.medianSeconds} /></span>
                  <span className={styles.changeLine}>{change(row.current.medianSeconds, row.previous.medianSeconds)}</span>
                </td>
                <td className={styles.count}>
                  <span className={styles.cellFigure}>{formatDuration(row.current.p90Seconds)}<Change current={row.current.p90Seconds} previous={row.previous.p90Seconds} /></span>
                  <span className={styles.changeLine}>{change(row.current.p90Seconds, row.previous.p90Seconds)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    <CountedFrom at={report.countedFrom} />
  </>;
}

/**
 * Team usage (TOADO-738), for owners: what each person did over the period,
 * with their avatar and the AI agents they work with (Jason, 16 Oct); then
 * those agents across the team: how many people use each, and what each did.
 * `banner`, at the top under the title, is This month by person (t-q6n-Ms).
 */
function TeamReport({ report, banner }: { report: ReportTeam; banner?: ReactNode }) {
  const exportCsv = () => downloadCsv('toado-team-usage.csv', toCsv(
    ['Person', 'Role', 'Tickets opened', 'Closed while theirs', 'Comments', 'Days active', 'Last active', 'AI agents'],
    report.people.map((person) => [person.name, person.role, person.opened, person.closed, person.comments, person.activeDays, person.lastActiveAt ?? '', (person.agents ?? []).join('; ')]),
  ));
  const people = useTableSort(report.people, {
    person: (person) => person.name, opened: (person) => person.opened, closed: (person) => person.closed,
    comments: (person) => person.comments, days: (person) => person.activeDays, last: (person) => person.lastActiveAt,
  });
  const agents = useTableSort(report.agents, {
    agent: (agent) => agent.agent, people: (agent) => agent.people, opened: (agent) => agent.opened,
    comments: (agent) => agent.comments, moves: (agent) => agent.moves, fixes: (agent) => agent.fixes ?? 0,
  });
  const most = (values: number[]) => Math.max(0, ...values);
  const top = {
    opened: most(report.people.map((person) => person.opened)), closed: most(report.people.map((person) => person.closed)),
    comments: most(report.people.map((person) => person.comments)),
  };
  return <>
    <CardHead title="Team usage" icon={UserCog} tone="magenta" takeaway={teamTakeaway(report)}
      info="For owners. What each person did this period: tickets opened, tickets closed while assigned to them, comments, and days with anything recorded. AI agents are the agents (Claude Code, Cursor and the like) that worked here through that person's MCP connections."
      onExport={exportCsv} canExport={report.people.length > 0} />
    {banner}
    <div className={`${styles.box} ${styles.tableWrap}`} data-testid="team-people">
      <table className={styles.table}>
        <thead><tr>
          {people.head('person', 'Person', { left: true, numeric: false })}<th scope="col" className={styles.leftHead}>AI agents</th>
          {people.head('opened', 'Opened')}{people.head('closed', 'Closed')}{people.head('comments', 'Comments')}{people.head('days', 'Days active')}{people.head('last', 'Last active', { numeric: false })}
        </tr></thead>
        <tbody>
          {people.rows.map((person) => (
            <tr key={person.userId}>
              <td>
                <span className={styles.person}>
                  <Avatar name={person.name} size="sm" />
                  <span>{person.name} <span className={styles.note}>{person.role}</span></span>
                </span>
              </td>
              <td>
                {/* The chips wrap inside their own box; the cell stays a table cell, so its row's rule runs unbroken. */}
                <div className={styles.agentsCell}>
                  {(person.agents ?? []).length === 0
                    ? <span className={styles.note}>None</span>
                    : (person.agents ?? []).map((agent) => <span key={agent} className={styles.agentChip}><Bot size={12} aria-hidden="true" />{agent}</span>)}
                </div>
              </td>
              <BarCell value={person.opened} max={top.opened} />
              <BarCell value={person.closed} max={top.closed} tone="var(--chart-green)" />
              <BarCell value={person.comments} max={top.comments} tone="var(--chart-violet)" />
              <td className={styles.count}>{person.activeDays}</td>
              <td className={styles.change}>{person.lastActiveAt ? <When at={person.lastActiveAt} /> : 'never'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <PersonUses report={report} />
    <PaneHead title="The AI agents your team works with" info={<>
      Each AI agent that worked in this workspace this period through someone's MCP connection, named as the connection names
      it: how many people use it, and the tickets it opened, its comments, its moves and the fixes it recorded. An agent that
      only read tickets does not show here.
    </>} />
    {report.agents.length === 0 ? (
      <EmptyState art="agents"><p>No one's AI agent did anything here in this period. <Link to="/me/settings/mcp-setup">Connect an agent</Link></p></EmptyState>
    ) : (
      <div className={`${styles.box} ${styles.tableWrap}`} data-testid="team-agents">
        <table className={styles.table}>
          <thead><tr>
            {agents.head('agent', 'Agent', { left: true, numeric: false })}{agents.head('people', 'People')}{agents.head('opened', 'Opened')}{agents.head('comments', 'Comments')}{agents.head('moves', 'Moves')}{agents.head('fixes', 'Fixes')}
          </tr></thead>
          <tbody>
            {agents.rows.map((agent) => (
              <tr key={agent.agent}>
                <td><span className={styles.agentChip}><Bot size={12} aria-hidden="true" />{agent.agent}</span></td>
                <td className={styles.count}>{agent.people ?? ''}</td>
                <td className={styles.count}>{agent.opened}</td>
                <td className={styles.count}>{agent.comments}</td>
                <td className={styles.count}>{agent.moves}</td>
                <td className={styles.count}>{agent.fixes ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>;
}

/** How a person filed their tickets, in the order a chart shows them. */
const FILED_HOW: Array<BarSeries & { key: keyof ReportTeam['people'][number]['uses']['filed'] }> = [
  { key: 'pins', label: 'Pins', tone: 'blue' },
  { key: 'edits', label: 'Element edits', tone: 'cyan' },
  { key: 'markup', label: 'Marked-up screenshots', tone: 'violet' },
  { key: 'screenshots', label: 'Screenshots', tone: 'green' },
  { key: 'walkthrough', label: 'From walkthroughs', tone: 'magenta' },
  { key: 'byHand', label: 'By hand', tone: 'grey' },
  { key: 'agent', label: 'By their AI agent', tone: 'amber' },
];

/**
 * Read short (Codex review round 4): every Bug collection card says its figures
 * are of the newest reports only, on the card and in its CSV, and its counts
 * carry the "+" of a lower bound.
 */
const READ_SHORT = 'This period has more reports than one report reads, so these figures are of the newest only. Choose a shorter period or one project for all of them.';
function ReadShort({ report }: { report: ReportCollection }) {
  return report.truncated ? <p className={styles.note} data-testid="collection-read-short">{READ_SHORT}</p> : null;
}
const readShortRows = (report: ReportCollection): Array<Array<string | number>> => (report.truncated ? [[`Note: ${READ_SHORT}`]] : []);

/** Minutes, said plainly: under one is "under a minute". */
const minutes = (seconds: number) => (seconds === 0 ? '0' : seconds < 60 ? 'under 1' : String(Math.round(seconds / 60)));

/**
 * What each person uses (Jason, 16 Oct): how they file tickets, and their
 * share of the plan, the walkthroughs they recorded, the recording and
 * narration minutes those used in the period, and the storage they hold now.
 */
function PersonUses({ report }: { report: ReportTeam }) {
  const people = report.people.filter((person) => person.uses);
  const filed = people.filter((person) => FILED_HOW.some((how) => person.uses.filed[how.key] > 0));
  const most = Math.max(1, ...people.map((person) => person.uses.storageBytes));
  const planUse = useTableSort(people, {
    person: (person) => person.name, walkthroughs: (person) => person.uses.walkthroughs, recording: (person) => person.uses.recordingSeconds,
    narration: (person) => person.uses.narrationSeconds, storage: (person) => person.uses.storageBytes,
  });
  return <>
    <PaneHead title="How each person files tickets" info={<>
      The tickets each person filed this period, by how: a pin on an element, an element edit, a marked-up or plain screenshot from
      the extension, from a walkthrough, by hand on the board, or by their AI agent through their connection.
    </>} />
    <BarChart hideCaption caption="Tickets each person filed this period, by how" testId="chart-filed-how" series={FILED_HOW}
      rows={filed.map((person) => ({ key: person.userId, label: person.name, values: person.uses.filed }))}
      empty={<p className={styles.note}>No one filed a ticket in this period.</p>} />
    <PaneHead title="Who uses the plan" info={<>
      Each person's walkthroughs this period, the recording minutes they used and the narration minutes they were billed, as the
      plan counts them; and the storage their walkthroughs and tickets take up now, whenever they were made.
    </>} />
    <div className={`${styles.box} ${styles.tableWrap}`} data-testid="team-plan-use">
      <table className={styles.table}>
        <thead><tr>
          {planUse.head('person', 'Person', { left: true, numeric: false })}{planUse.head('walkthroughs', 'Walkthroughs')}{planUse.head('recording', 'Recording minutes')}
          {planUse.head('narration', 'Narration minutes')}{planUse.head('storage', 'Storage now')}
        </tr></thead>
        <tbody>
          {planUse.rows.map((person) => (
            <tr key={person.userId}>
              <td><span className={styles.person}><Avatar name={person.name} size="sm" />{person.name}</span></td>
              <td className={styles.count}>{person.uses.walkthroughs}</td>
              <td className={styles.count}>{minutes(person.uses.recordingSeconds)}</td>
              <td className={styles.count}>{minutes(person.uses.narrationSeconds)}</td>
              <td className={styles.count}>
                <span className={styles.storageCell}>
                  <span className={styles.meter} aria-hidden="true"><span style={{ width: share(person.uses.storageBytes, most) }} /></span>
                  {formatBytes(person.uses.storageBytes)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>;
}

/** What Reports offers, for a workspace below Team, with where to upgrade (as the Jira tab does). */
function ReportsNeedTeam({ companyId }: { companyId: string }) {
  return (
    <div className={styles.upgrade} data-testid="reports-need-team">
      <h2>See how your team is doing, on the Team plan</h2>
      <p>Reports are part of the Team, Business and Enterprise plans. Upgrade to see, over any period and against the one before:</p>
      <ul>
        <li>How many tickets were opened, per project</li>
        <li>The errors your captures caught most, grouped by message</li>
        <li>The pages people report bugs on most</li>
        <li>Tickets opened against tickets closed, week by week</li>
        <li>How long a ticket takes from capture to done, per project</li>
        <li>For owners, what each person and each AI agent did</li>
      </ul>
      <div><Link className={styles.upgradeButton} to={`/company/settings/billing?workspace=${encodeURIComponent(companyId)}`}>Upgrade to Team</Link></div>
    </div>
  );
}

const KIND_SERIES: Array<{ key: CollectionKind; label: string; tone: HeadTone; icon: LucideIcon }> = [
  { key: 'form', label: 'Form', tone: 'blue', icon: FileText },
  { key: 'pin', label: 'Pin', tone: 'violet', icon: Crosshair },
  { key: 'recording', label: 'Recording', tone: 'cyan', icon: Video },
];

/**
 * Bug collection, by widget (TOADO-964): how many reports each project's
 * widget brought in, each kind apart, against the period before, and the
 * sites they came from.
 */
function CollectionWidgetsReport({ report, against }: { report: ReportCollection; against: string }) {
  const short = report.truncated;
  const top = Math.max(0, ...report.widgets.map((widget) => widget.opened.current));
  const table = useTableSort(report.widgets, {
    widget: (row) => row.project.name, current: (row) => row.opened.current, previous: (row) => row.opened.previous,
    form: (row) => row.byKind.form, pin: (row) => row.byKind.pin, recording: (row) => row.byKind.recording,
  });
  const exportCsv = () => downloadCsv('toado-bug-collection-widgets.csv', toCsv(
    ['Widget', 'Reports', 'Reports before', 'Form', 'Pin', 'Recording', 'Sites'],
    [...report.widgets.map((row) => [row.project.name, figure(row.opened.current, short), short ? 'not read in full' : row.opened.previous,
      figure(row.byKind.form, short), figure(row.byKind.pin, short), figure(row.byKind.recording, short),
      row.sites.map((site) => `${site.host} (${figure(site.reports, short)})`).join('; ')]),
    ...readShortRows(report)],
  ));
  return <>
    <CardHead title="Reports by widget" icon={Megaphone} tone="blue" takeaway={collectionWidgetsTakeaway(report)}
      info="Every report a visitor sent through a project's bug collection widget, its public report link or a team snippet: a form, a pin on an element, or a recording. Against the period before, with the sites they came from."
      onExport={exportCsv} canExport={report.widgets.length > 0} />
    <ReadShort report={report} />
    <div className={styles.stats}>
      <Stat icon={Megaphone} tone="blue" label="Reports" testId="collection-reports" value={figure(report.opened.current, short)}
        detail={short ? 'Not compared: too many to read' : comparison(report.opened.current, report.opened.previous, against)} />
      {KIND_SERIES.map((kind) => (
        <Stat key={kind.key} icon={kind.icon} tone={kind.tone}
          label={kind.label} testId={`collection-${kind.key}`} value={figure(report.opened.byKind[kind.key], short)}
          detail={share(report.opened.byKind[kind.key], report.opened.current)} />
      ))}
    </div>
    {report.widgets.length === 0 ? (
      <EmptyState art="bars"><p>No visitor sent a report through a bug collection widget in this period or the one before.</p></EmptyState>
    ) : (
      <div className={`${styles.box} ${styles.tableWrap}`} data-testid="collection-widgets">
        <table className={styles.table}>
          <thead><tr>
            {table.head('widget', 'Widget', { left: true, numeric: false })}{table.head('current', 'Reports')}{table.head('previous', 'Before')}
            {table.head('form', 'Form')}{table.head('pin', 'Pin')}{table.head('recording', 'Recording')}
            <th scope="col" className={styles.leftHead}>Sites</th>
          </tr></thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.project.id}>
                <td>
                  <Link to={`/projects/${row.project.id}/board`} className={styles.project}><ProjectMark project={row.project} size={16} />{row.project.name}</Link>
                </td>
                <BarCell value={row.opened.current} max={top}>{figure(row.opened.current, short)}</BarCell>
                <td className={styles.count}>{short ? '-' : row.opened.previous}</td>
                <td className={styles.count}>{figure(row.byKind.form, short)}</td>
                <td className={styles.count}>{figure(row.byKind.pin, short)}</td>
                <td className={styles.count}>{figure(row.byKind.recording, short)}</td>
                <td>
                  {row.sites.length === 0 ? <span className={styles.note}>No page recorded</span> : (
                    <ul className={styles.siteList}>
                      {row.sites.map((site) => <li key={site.host}><span className={styles.pageAddress}>{site.host}</span><span className={styles.count}>{figure(site.reports, short)}</span></li>)}
                    </ul>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>;
}

/** The pages visitors reported from the most, without their query. */
function CollectionPagesReport({ report }: { report: ReportCollection }) {
  const top = Math.max(0, ...report.pages.map((page) => page.reports));
  const exportCsv = () => downloadCsv('toado-bug-collection-pages.csv', toCsv(
    ['Page', 'Reports'], [...report.pages.map((page) => [`${page.host}${page.path}`, figure(page.reports, report.truncated)]), ...readShortRows(report)],
  ));
  return <>
    <CardHead title="Pages reported from" icon={Bug} tone="red" takeaway={collectionPagesTakeaway(report)}
      info="The pages visitors were on when they reported, by address without its query or fragment, most first." onExport={exportCsv} canExport={report.pages.length > 0} />
    <ReadShort report={report} />
    {report.pages.length === 0 ? <EmptyState art="pages"><p>No report in this period recorded its page.</p></EmptyState> : (
      <table className={styles.barTable} data-testid="collection-pages">
        <caption className={styles.visuallyHidden}>Reports per page</caption>
        <thead className={styles.visuallyHidden}><tr><th scope="col">Page</th><th scope="col">Reports</th></tr></thead>
        <tbody>
          {report.pages.map((page) => (
            <tr key={`${page.host}${page.path}`}>
              <td className={styles.barLabel}><span className={styles.pageAddress}>{page.host}{page.path}</span></td>
              <td className={styles.barCell}>
                <span className={styles.barTrack} aria-hidden="true"><span className={styles.barNow} style={{ width: share(page.reports, top) }} /></span>
                <span className={styles.count}>{figure(page.reports, report.truncated)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </>;
}

/**
 * Visitors (TOADO-962): who reported through the widget, recognised by email
 * and by address (the API's visitorIdentity.ts), with how often and whether
 * they had reported before. An address is never shown.
 */
function CollectionVisitorsReport({ report }: { report: ReportCollection }) {
  const { visitors } = report;
  const table = useTableSort(visitors.list, {
    visitor: (row) => visitorLabel(row), current: (row) => row.current, total: (row) => row.total, open: (row) => row.open,
    widgets: (row) => row.widgets, first: (row) => row.firstAt, last: (row) => row.lastAt,
  });
  const most = Math.max(0, ...visitors.list.map((row) => row.current));
  const unnamedNumber = new Map(visitors.list.filter((row) => !row.name && !row.email).map((row, index) => [row.key, index + 1]));
  const label = (row: (typeof visitors.list)[number]) => (unnamedNumber.has(row.key) ? `${visitorLabel(row)} (${unnamedNumber.get(row.key)})` : visitorLabel(row));
  // Read short, the history may leave out a visitor's earliest reports: what is
  // counted from it is at least what is shown, and their first is the first read (Codex review round 3).
  // And either cut makes the period's visitor figures lower bounds (visitorsUncertain).
  const short = visitors.historyTruncated;
  const cut = report.truncated;
  const unsure = visitorsUncertain(report);
  const firstLabel = short ? 'First report read' : 'First report';
  const exportCsv = () => downloadCsv('toado-bug-collection-visitors.csv', toCsv(
    ['Name', 'Email', unsure ? 'Reports (at least)' : 'Reports', short ? 'Reports in all (at least)' : 'Reports in all', short ? 'Open (at least)' : 'Open',
      short ? 'Widgets (at least)' : 'Widgets', firstLabel, 'Last report', short ? 'Returning (as far as read)' : 'Returning'],
    [...visitors.list.map((row) => [row.name ?? (row.email ? '' : label(row)), row.email ?? '', row.current, row.total, row.open, row.widgets, row.firstAt, row.lastAt, row.returning ? 'yes' : 'no']),
      ...readShortRows(report)],
  ));
  return <>
    <CardHead title="Visitors" icon={Users} tone="violet" takeaway={visitorsTakeaway(report)}
      info={`A visitor is known by the email they gave, from any address. A report without an email is theirs when they used that email from the same address within 30 days; an address used with two or more emails is a shared network, such as an office, and names no one.`}
      onExport={exportCsv} canExport={visitors.list.length > 0} />
    <ReadShort report={report} />
    <div className={styles.stats}>
      <Stat icon={Users} tone="violet" label="Visitors" testId="collection-visitors" value={figure(visitors.inPeriod, unsure)}
        detail={`${figure(visitors.named, unsure)} gave an email`} />
      <Stat icon={Repeat} tone="green" label="Returning" testId="collection-returning" value={figure(visitors.returning, unsure)}
        detail="had reported before this period" />
      <Stat icon={Minus} tone="amber" label="No one to name" testId="collection-unnamed" value={figure(visitors.unnamedReports, cut)}
        detail={visitors.historyTruncated
          ? 'reports from a shared network, with no email and no address, or that earlier reports left out of this read would be needed to judge'
          : 'reports from a shared network, or with no email and no address'} />
    </div>
    {visitors.list.length === 0 ? (
      <EmptyState art="bars"><p>{visitors.unnamedReports > 0
        ? `Reports came in this period, but none can be put on a visitor: they came from a shared network, or with no email and no address${short ? ', or judging them needs earlier reports this read left out' : ''}.`
        : 'No visitor reported in this period.'}</p></EmptyState>
    ) : (
      <div className={`${styles.box} ${styles.tableWrap}`} data-testid="collection-visitor-list">
        <table className={styles.table}>
          <thead><tr>
            {table.head('visitor', 'Visitor', { left: true, numeric: false })}{table.head('current', 'Reports')}{table.head('total', 'In all')}
            {table.head('open', 'Open')}{table.head('widgets', 'Widgets')}{table.head('first', firstLabel, { numeric: false })}{table.head('last', 'Last report', { numeric: false })}
          </tr></thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.key}>
                <td>
                  <span className={styles.visitorName}>{label(row)}</span>
                  {row.returning && <span className={`${styles.tag} ${styles.tagReturning}`}>Returning</span>}
                  {row.name && row.email && <span className={styles.visitorEmail}>{row.email}</span>}
                </td>
                <BarCell value={row.current} max={most} tone="var(--chart-violet)">{figure(row.current, unsure)}</BarCell>
                <td className={styles.count}>{figure(row.total, short)}</td>
                <td className={styles.count}>{figure(row.open, short)}</td>
                <td className={styles.count}>{figure(row.widgets, short)}</td>
                <td className={styles.count}>{day(row.firstAt)}</td>
                <td className={styles.count}>{day(row.lastAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {visitors.inPeriod > visitors.list.length && <p className={styles.note}>The {visitors.list.length} who reported most, of {figure(visitors.inPeriod, unsure)}.</p>}
    {visitors.historyTruncated && <p className={styles.note} data-testid="collection-history-short">There are more earlier reports than one report reads, so for each visitor the reports in all, open and widgets are at least what is shown, whether they came back is as far as was read, and their first report is the first read.</p>}
  </>;
}

/** Where the period's reports went: intake, the board, closed or archived, and the typical time to close. */
function CollectionOutcomesReport({ report }: { report: ReportCollection }) {
  const { outcomes } = report;
  const total = outcomes.intake + outcomes.board + outcomes.closed + outcomes.archived;
  const rows: Array<{ key: string; label: string; value: number; tone: string }> = [
    { key: 'intake', label: 'Waiting in intake', value: outcomes.intake, tone: 'var(--chart-amber)' },
    { key: 'board', label: 'On the board', value: outcomes.board, tone: 'var(--chart-blue)' },
    { key: 'closed', label: 'Closed', value: outcomes.closed, tone: 'var(--chart-green)' },
    { key: 'archived', label: 'Archived', value: outcomes.archived, tone: 'var(--text-tertiary)' },
  ];
  const cut = report.truncated;
  const exportCsv = () => downloadCsv('toado-bug-collection-outcomes.csv', toCsv(
    ['Where', 'Reports'], [...rows.map((row) => [row.label, figure(row.value, cut)] as Array<string | number>),
      ['Typical hours to close', outcomes.medianHoursToClose === null ? '' : Math.round(outcomes.medianHoursToClose * 100) / 100],
      ...readShortRows(report)],
  ));
  return <>
    <CardHead title="What happened to them" icon={CheckCircle2} tone="green" takeaway={outcomesTakeaway(report)}
      info="Of the reports that came in this period: where each is now. Closed means it reached its project's last column and is still there."
      onExport={exportCsv} canExport={total > 0} />
    <ReadShort report={report} />
    {total === 0 ? <EmptyState art="clear"><p>No reports came in this period.</p></EmptyState> : (
      <table className={styles.barTable} data-testid="collection-outcomes">
        <caption className={styles.visuallyHidden}>Where the period's reports are now</caption>
        <thead className={styles.visuallyHidden}><tr><th scope="col">Where</th><th scope="col">Reports</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={styles.barLabel}>{row.label}</td>
              <td className={styles.barCell}>
                <span className={styles.barTrack} aria-hidden="true"><span className={styles.barNow} style={{ width: share(row.value, total), background: row.tone }} /></span>
                <span className={styles.count}>{figure(row.value, cut)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
    {outcomes.medianHoursToClose !== null && <p className={styles.note}>{cut ? 'Among the reports read, typically' : 'Typically'} closed in {formatDuration(outcomes.medianHoursToClose * 3600)}.</p>}
  </>;
}

/** Owners: this month's recording minutes per widget against its cap, and the period's recordings. */
function CollectionRecordingReport({ recording }: { recording: NonNullable<ReportCollection['recording']> }) {
  // The month the minutes are for, which is today's whatever period is chosen (z.ai review round 3).
  const month = new Date(`${recording.month.slice(0, 7)}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); // a calendar month: a day, not a moment (TOADO-904)
  const exportCsv = () => downloadCsv('toado-bug-collection-recording.csv', toCsv(
    ['Widget', 'Month', 'Minutes used', 'Monthly cap (minutes)', 'Turned away', 'Recordings sent in the period', 'With voice'],
    recording.widgets.map((row) => [row.project.name, recording.month.slice(0, 7), minutes(row.usedSeconds), minutes(row.capSeconds), row.refused, row.sent, row.narrated]),
  ));
  return <>
    <CardHead title="Visitor recording" icon={Video} tone="cyan"
      takeaway={recording.widgets.some((row) => row.refused > 0) ? `A widget turned visitors away in ${month}: its monthly minutes ran out.` : null}
      info={`Recording minutes for each widget in ${month} against its monthly cap, and the visitors the cap turned away then; and the recordings visitors sent in the chosen period, with how many talked.`}
      onExport={exportCsv} canExport={recording.widgets.length > 0} />
    {recording.widgets.length === 0 ? <EmptyState art="clear"><p>No widget has visitor recording switched on.</p></EmptyState> : (
      <div className={`${styles.box} ${styles.tableWrap}`} data-testid="collection-recording">
        <table className={styles.table}>
          <thead><tr>
            <th scope="col">Widget</th><th scope="col" className={styles.leftHead}>{month}</th><th scope="col">Turned away</th><th scope="col">Sent</th><th scope="col">With voice</th>
          </tr></thead>
          <tbody>
            {recording.widgets.map((row) => (
              <tr key={row.project.id}>
                <td><span className={styles.project}><ProjectMark project={row.project} size={16} />{row.project.name}</span>
                  {!row.switchedOn && <span className={styles.note}> (switched off)</span>}</td>
                <td>
                  <span className={styles.usageLine}>{minutes(row.usedSeconds)} of {minutes(row.capSeconds)} min</span>
                  <span className={styles.meter} style={{ '--project': 'var(--chart-cyan)' } as CSSProperties} aria-hidden="true">
                    <span style={{ width: share(row.usedSeconds, row.capSeconds) }} />
                  </span>
                </td>
                <td className={styles.count}>{row.refused}</td>
                <td className={styles.count}>{row.sent}</td>
                <td className={styles.count}>{row.narrated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>;
}
